import type { SxProps, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

import { customGradients, customShadows } from "@/theme/customTokens";

export const root: SxProps<Theme> = {
  background: customGradients.heroQuickOrder,
  borderRadius: 3,
  boxShadow: customShadows.heroQuickOrder,
  p: { xs: 2.5, md: 3.25 },
  display: "flex",
  flexDirection: "column",
  gap: 2.5,
  width: "100%",
};

export const heroHeader: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.25,
};

export const heroIconWrap: SxProps<Theme> = {
  width: 40,
  height: 40,
  borderRadius: 1.5,
  bgcolor: alpha("#ffffff", 0.15),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

export const heroTitle: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: "1.0625rem",
  color: "#fff",
};

export const heroSubtitle: SxProps<Theme> = {
  fontSize: "0.78125rem",
  color: alpha("#ffffff", 0.8),
  mt: 0.25,
};

export const innerCard: SxProps<Theme> = {
  bgcolor: "background.paper",
  borderRadius: 2.25,
  p: 2.5,
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "1fr 300px" },
  gap: 2.5,
  alignItems: "start",
};

export const pickerColumn: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1.75,
  minWidth: 0,
};

export const productGrid: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: 1.25,
};

export const productTile =
  (selected: boolean): SxProps<Theme> =>
  (theme) => ({
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    p: 1.5,
    borderRadius: 1.75,
    border: `1.5px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
    bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : "background.paper",
    cursor: "pointer",
    transition: "all 0.15s ease",
    minWidth: 0,
    "&:hover": {
      borderColor: theme.palette.primary.light,
      bgcolor: alpha(theme.palette.primary.main, 0.08),
    },
  });

export const productInfo: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

export const productName: SxProps<Theme> = {
  fontSize: "0.8125rem",
  fontWeight: 600,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const productPrice: SxProps<Theme> = {
  fontSize: "0.71875rem",
  color: "text.secondary",
};

export const qtyBadge: SxProps<Theme> = (theme) => ({
  flexShrink: 0,
  width: 26,
  height: 26,
  borderRadius: "50%",
  bgcolor: "primary.main",
  color: theme.palette.primary.contrastText,
  fontSize: "0.75rem",
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

export const addButton: SxProps<Theme> = (theme) => ({
  flexShrink: 0,
  width: 34,
  height: 34,
  borderRadius: 1.25,
  border: "none",
  bgcolor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "all 0.15s ease",
  "& svg [stroke]": {
    stroke: theme.palette.primary.main,
  },
  "&:hover": {
    bgcolor: "primary.main",
    color: theme.palette.primary.contrastText,
    "& svg [stroke]": {
      stroke: theme.palette.primary.contrastText,
    },
  },
});

export const cartColumn = (theme: Theme): SxProps<Theme> => ({
  display: "flex",
  flexDirection: "column",
  gap: 1.75,
  borderInlineStart: { md: `1px solid ${theme.palette.divider}` },
  ps: { md: 2.5 },
  minWidth: 0,
});

export const emptyCart = (theme: Theme): SxProps<Theme> => ({
  flex: 1,
  minHeight: 180,
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: 1.75,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 1.25,
  textAlign: "center",
  p: 2.5,
});

export const cartList: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1.25,
  maxHeight: 190,
  overflowY: "auto",
};

export const cartItem: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  minWidth: 0,
};

export const cartItemInfo: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

export const cartItemName: SxProps<Theme> = {
  fontSize: "0.78125rem",
  fontWeight: 600,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const cartItemPrice: SxProps<Theme> = {
  fontSize: "0.6875rem",
  color: "text.secondary",
};

export const totalRow = (theme: Theme): SxProps<Theme> => ({
  borderTop: `1px dashed ${theme.palette.divider}`,
  pt: 1.5,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

export const totalLabel: SxProps<Theme> = {
  fontSize: "0.78125rem",
  color: "text.secondary",
};

export const totalValue: SxProps<Theme> = {
  fontWeight: 800,
  fontSize: "1rem",
};

export const totalCurrency: SxProps<Theme> = {
  fontSize: "0.6875rem",
  fontWeight: 500,
  color: "text.secondary",
};

export const ctaButton: SxProps<Theme> = {
  width: "100%",
};

export const linkReady: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1.25,
};

export const linkReadyLabel: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  fontSize: "0.78125rem",
  fontWeight: 700,
  color: "success.main",
};

export const resetLink: SxProps<Theme> = {
  background: "transparent",
  border: "none",
  color: "text.secondary",
  fontFamily: "inherit",
  fontSize: "0.75rem",
  cursor: "pointer",
  textDecoration: "underline",
  p: 0,
  textAlign: "center",
};

export const loadingWrap: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  py: 4,
};

export const ctaError: SxProps<Theme> = {
  textAlign: "center",
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "error.main",
  lineHeight: 1.7,
  mb: 1,
};
